import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { WaitState } from "../components/WaitState";
import { portalPackagesApi } from "../api/packages";
import type { InHouseConsentDto, PortalItemDto } from "../api/types";
import { useLetterPreview } from "../hooks/useLetterPreview";
import { ResponseBlockedHint, type PortalResponse } from "../components/ResponseStep";
import { chainActionLabel, chainDoneSentence } from "../lib/itemStatus";

// External Intake & Collection v1.0 PR 8 §9 — in-house signature
// surface. Flow:
//   1. GET the consent disclosure (versioned). If the signer has not
//      yet accepted the current version, render the disclosure with
//      an explicit "I agree" affirmative-consent button.
//   2. Capture the signature — either typed (rendered to a PNG canvas)
//      or drawn on a touch/pointer canvas. Both flatten to a base64
//      PNG data URI before submit.
//   3. POST the signature; server renders the artifact + signature
//      into a final flattened PDF, hashes it, audits, and stages.
//
// Reuse: no new PDF or signing libraries — the canvas is a vanilla
// 2D context.
//
// Signing truth (A19) — while the signature POST is in flight the document
// is being sealed server-side; leaving would abandon it without the
// recipient knowing whether they signed. So: the browser's leave-page
// warning is armed, the parent is told (onBusyChange) so it can lock its
// own navigation, and the wait copy promises no duration it can't keep.

interface Props {
  item: PortalItemDto;
  onUpdated?: (next: PortalItemDto) => void;
  /** Review loop — set while the item owes a response; the note travels with
   *  the signature and the submit is disabled while it is blank. */
  response?: PortalResponse | null;
  /** True while the signature POST is in flight, false once it settles. */
  onBusyChange?: (busy: boolean) => void;
}

const CANVAS_MAX_WIDTH = 500;
const CANVAS_MIN_WIDTH = 220;
const CANVAS_HEIGHT = 160;

/** Pad width for the container the pad mounts into — full width on phones,
 *  capped at the original 500px on desktop. */
export function clampCanvasWidth(containerWidth: number): number {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return CANVAS_MAX_WIDTH;
  return Math.min(CANVAS_MAX_WIDTH, Math.max(CANVAS_MIN_WIDTH, Math.floor(containerWidth)));
}

export function InHouseSigningSurface({ item, onUpdated, response, onBusyChange }: Props) {
  const [consent, setConsent] = useState<InHouseConsentDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const [mode, setMode] = useState<"drawn" | "typed">("drawn");
  const [typedName, setTypedName] = useState("");
  // Approval chains — the same consent + signature endpoints complete the
  // caller's chain stage (the server decides Endorse vs Sign); only the
  // labels and the "done" truth come from the chain view, never item status.
  const chain = item.approvalChain ?? null;
  const isChain = chain !== null;
  const endorse = chain?.myAction === "Endorse";
  const chainDone = chain?.myStatus === "Done";
  // Sender-Fields v1 — review the pre-filled letter PDF before signing (same
  // render as the final signed artifact); shared hook with the review surface.
  // A chain item's preview is its current composed PDF.
  const showsDocument = (item.hasLetterBody ?? false) || chain !== null;
  const { url: letterUrl } = useLetterPreview(item.id, showsDocument);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasDrawingRef = useRef(false);
  // Pad size locks when the pad mounts (rotating mid-signature means
  // reopening the pad). The canvas's pixel buffer (width attribute) and its
  // CSS size are kept identical so pointer coordinates map 1:1 to pixels —
  // never scale the buffer via CSS alone.
  const [canvasWidth, setCanvasWidth] = useState(CANVAS_MAX_WIDTH);
  const measurePad = useCallback((el: HTMLDivElement | null) => {
    if (el) setCanvasWidth(clampCanvasWidth(el.clientWidth));
  }, []);

  useEffect(() => {
    if (
      isChain
        ? chainDone
        : item.status === "SubmittedForReview" || item.status === "Approved"
    ) {
      setCompleted(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const dto = await portalPackagesApi.getInHouseConsent(item.id);
        if (!cancelled) setConsent(dto);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Couldn't load the consent disclosure.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.id, item.status, isChain, chainDone]);


  const acceptConsent = useSubmittingMutation(
    async () => {
      if (!consent) return;
      await portalPackagesApi.acceptInHouseConsent(item.id, consent.consentVersion);
      setConsent({ ...consent, alreadyAccepted: true });
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't record your consent."),
    },
  );

  const handleAcceptConsent = useCallback(() => {
    if (!consent) return;
    setError(null);
    void acceptConsent.submit();
  }, [consent, acceptConsent]);

  function startDraw(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    drawingRef.current = true;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function moveDraw(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    hasDrawingRef.current = true;
  }

  function endDraw() {
    drawingRef.current = false;
  }

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function clearDraw() {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !canvasRef.current) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    hasDrawingRef.current = false;
  }

  function renderTypedToPng(): string | null {
    if (!typedName.trim()) return null;
    // The typed PNG is a server-bound artifact — always render it at the
    // full 500px regardless of the on-screen pad size.
    const c = document.createElement("canvas");
    c.width = CANVAS_MAX_WIDTH;
    c.height = CANVAS_HEIGHT;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "italic 48px 'Georgia', serif";
    ctx.textBaseline = "middle";
    ctx.fillText(typedName.trim(), 12, CANVAS_HEIGHT / 2);
    return c.toDataURL("image/png");
  }

  function captureDrawn(): string | null {
    if (!hasDrawingRef.current || !canvasRef.current) return null;
    return canvasRef.current.toDataURL("image/png");
  }

  const signature = useSubmittingMutation(
    async (consentVersion: string, dataUri: string) => {
      await portalPackagesApi.submitInHouseSignature(item.id, {
        consentVersion,
        signatureDataUri: dataUri,
        signatureMethod: mode,
        ...(response ? { responseNote: response.note } : {}),
      });
      setCompleted(true);
      if (onUpdated && chain) {
        // Approval chains — the server completed the caller's stage; the item
        // is not "submitted for review". The caller re-reads the chain for
        // every other stage's state.
        onUpdated({
          ...item,
          approvalChain: { ...chain, myStatus: "Done", canAct: false, canReturn: false },
        });
      } else if (onUpdated) {
        // The signature resubmits the item, which answers any open return.
        onUpdated({
          ...item,
          status: "SubmittedForReview",
          responseOwed: false,
          canSendBackWithoutChanges: false,
        });
      }
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't submit your signature."),
    },
  );
  const submitting = signature.pending;

  useEffect(() => {
    if (!submitting) return;
    onBusyChange?.(true);
    const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers only show the prompt when returnValue is set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeLeaving);
      onBusyChange?.(false);
    };
  }, [submitting, onBusyChange]);

  function handleSubmit() {
    if (!consent || response?.blockedReason) return;
    setError(null);
    const dataUri = mode === "typed" ? renderTypedToPng() : captureDrawn();
    if (!dataUri) {
      setError(
        mode === "typed"
          ? "Type your name to sign."
          : "Draw your signature before submitting.",
      );
      return;
    }
    void signature.submit(consent.consentVersion, dataUri);
  }

  if (completed && chain) {
    return (
      <div
        className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="chain-step-recorded"
      >
        <h2 className="text-lead font-display text-onyx m-0">
          {endorse ? "Endorsement recorded" : "Signature recorded"}
        </h2>
        <p className="text-small text-onyx m-0">{chainDoneSentence(chain.myAction)}</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure">
        <h2 className="text-h4 font-display text-onyx m-0">Signed — under review</h2>
        <p className="text-small text-onyx m-0">
          Thanks for signing. Your document is being reviewed; we'll let you know if anything else
          is needed.
        </p>
      </div>
    );
  }

  if (!consent) {
    return (
      <div className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure">
        <p className="text-small text-gray-mid m-0">Loading the e-sign disclosure…</p>
        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      {showsDocument && letterUrl && (
        <section
          className="flex flex-col gap-space-2 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="letter-preview"
        >
          <h2 className="text-h4 font-display text-onyx m-0">
            {chain ? "Review the document" : "Review your letter"}
          </h2>
          <p className="text-small text-gray-mid m-0">
            {endorse
              ? "Please review the document below before endorsing. This is exactly what you are endorsing."
              : "Please review the document below before signing. This is exactly what you are signing."}
          </p>
          <iframe
            src={letterUrl}
            title="Letter preview"
            className="w-full rounded-md border border-border-light"
            style={{ height: "min(640px, 75dvh)" }}
          />
        </section>
      )}

      {!consent.alreadyAccepted && (
        <section
          className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="in-house-consent"
        >
          <h2 className="text-h4 font-display text-onyx m-0">
            {endorse ? "Before you endorse" : "Before you sign"}
          </h2>
          <p className="text-small text-onyx m-0 whitespace-pre-wrap">{consent.disclosureText}</p>
          <div>
            <SubmitButton
              onClick={handleAcceptConsent}
              pending={acceptConsent.pending}
              pendingLabel="Recording…"
            >
              I agree
            </SubmitButton>
          </div>
          <SubmittingHint pending={acceptConsent.pending} />
        </section>
      )}

      {consent.alreadyAccepted && (
        <section
          className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="in-house-signature"
        >
          <h2 className="text-h4 font-display text-onyx m-0">
            {endorse ? "Endorse here" : "Sign here"}
          </h2>
          <div className="flex items-center gap-space-3">
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="sig-mode"
                value="drawn"
                checked={mode === "drawn"}
                onChange={() => setMode("drawn")}
              />
              Draw
            </label>
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="sig-mode"
                value="typed"
                checked={mode === "typed"}
                onChange={() => setMode("typed")}
              />
              Type
            </label>
          </div>

          {mode === "drawn" && (
            <div className="flex flex-col gap-space-2" ref={measurePad}>
              <canvas
                ref={canvasRef}
                width={canvasWidth}
                height={CANVAS_HEIGHT}
                onPointerDown={startDraw}
                onPointerMove={moveDraw}
                onPointerUp={endDraw}
                onPointerLeave={endDraw}
                className="border border-border-light rounded-md bg-white-warm touch-none"
                style={{ width: `${canvasWidth}px`, height: `${CANVAS_HEIGHT}px` }}
                data-testid="signature-canvas"
              />
              <div>
                <Button variant="secondary" onClick={clearDraw}>Clear</Button>
              </div>
            </div>
          )}

          {mode === "typed" && (
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name"
              className="w-full border border-border-light rounded-md p-space-2 text-h4 italic"
              style={{ fontFamily: "Georgia, serif", maxWidth: `${CANVAS_MAX_WIDTH}px` }}
              data-testid="signature-typed-input"
            />
          )}

          <div>
            <SubmitButton
              onClick={handleSubmit}
              pending={submitting}
              pendingLabel="Submitting…"
              disabled={!!response?.blockedReason}
              data-testid="in-house-submit-signature"
            >
              {chain ? chainActionLabel(chain.myAction) : "Submit signed document"}
            </SubmitButton>
          </div>
          <ResponseBlockedHint response={response} />
          {submitting && (
            <WaitState
              label={endorse ? "Recording your endorsement…" : "Recording your signature…"}
              message="We're sealing the document and saving the audit trail. Please keep this tab open until it finishes."
            />
          )}
        </section>
      )}

      {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
    </div>
  );
}
